import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { TranslocoLoader, Translation } from '@jsverse/transloco';
import { map } from 'rxjs/operators';
import { VALIDATION_TRANSLATIONS } from '../../../../../../libs/shared/src/lib/i18n/validation-translations';
import { ERROR_TRANSLATIONS } from '../../../../../../libs/shared/src/lib/i18n/error-translations';
import { getConsents } from '../../../../../../libs/shared/src/lib/i18n/consents/index';
import { getDeletionRequests } from '../../../../../../libs/shared/src/lib/i18n/deletion-requests/index';
import { getJobPositions } from '../../../../../../libs/shared/src/lib/i18n/job-positions/index';
import { getJobEducationLevels } from '../../../../../../libs/shared/src/lib/i18n/job-education-levels/index';
import { getJobEmploymentTypes } from '../../../../../../libs/shared/src/lib/i18n/job-employment-types/index';
import { getJobWorkplaceTypes } from '../../../../../../libs/shared/src/lib/i18n/job-workplace-types/index';
import { getJobSalaryPeriods } from '../../../../../../libs/shared/src/lib/i18n/job-salary-periods/index';
import { getLocations } from '../../../../../../libs/shared/src/lib/i18n/locations/index';
import { getSkills } from '../../../../../../libs/shared/src/lib/i18n/skills/index';
import { getLanguages } from '../../../../../../libs/shared/src/lib/i18n/languages/index';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ValidationTranslateLoader implements TranslocoLoader {
  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}

  getTranslation(lang: string) {
    // Use versioned filename for cache busting (works in Safari)
    // In production: use versioned filename (en.123456789.json)
    // In development: use query parameter (en.json?v=dev-123)
    const buildVersion = (environment as any).buildVersion || Date.now();
    // During SSR, Node's fetch rejects relative URLs, so resolve against
    // the SSR server itself (PORT comes from Dockerfile.prod, 4000 is the
    // server.ts fallback). The server also skips cache busting entirely:
    // it has no HTTP cache, and the versioned filename may not exist in
    // the server bundle (the Dockerfile only rewrites the placeholder in
    // browser *.js files) — a 404 there would fall through to the SSR
    // catch-all and recurse until OOM.
    const url = isPlatformServer(this.platformId)
      ? `http://localhost:${(globalThis as any).process?.env?.['PORT'] || 4000}/assets/i18n/${lang}.json`
      : environment.production
        ? `/assets/i18n/${lang}.${buildVersion}.json`
        : `/assets/i18n/${lang}.json?v=${buildVersion}`;

    return this.http.get<Translation>(url).pipe(
      map((appTranslations): Translation => {
        // Merge app translations with shared validation translations
        const sharedValidationTranslations = VALIDATION_TRANSLATIONS[lang as keyof typeof VALIDATION_TRANSLATIONS] || {};

        // Merge app translations with shared error translations
        const sharedErrorTranslations = ERROR_TRANSLATIONS[lang as keyof typeof ERROR_TRANSLATIONS] || {};

        // Get consent translations from shared library
        const consentTranslations = getConsents(lang);

        // Get deletion request translations from shared library
        const deletionRequestTranslations = getDeletionRequests(lang);

        // Get job positions translations from shared library
        const jobPositionsTranslations = getJobPositions(lang as 'sk' | 'en' | 'cz');

        // Get education levels, employment types, workplace types, and salary periods translations from shared library
        const educationLevelsTranslations = getJobEducationLevels(lang as 'sk' | 'en' | 'cz');
        const employmentTypesTranslations = getJobEmploymentTypes(lang as 'sk' | 'en' | 'cz');
        const workplaceTypesTranslations = getJobWorkplaceTypes(lang as 'sk' | 'en' | 'cz');
        const salaryPeriodsTranslations = getJobSalaryPeriods(lang as 'sk' | 'en' | 'cz');

        // Get locations translations from shared library
        const locationsTranslations = getLocations(lang as 'sk' | 'en' | 'cz');

        // Get skills translations from shared library
        const skillsTranslations = getSkills(lang as 'sk' | 'en' | 'cz');

        // Get languages translations from shared library
        const languagesTranslations = getLanguages(lang as 'sk' | 'en' | 'cz');

        return {
          ...appTranslations,
          validation: {
            ...((appTranslations as any).validation || {}),
            ...((sharedValidationTranslations as any).validation || {})
          },
          errors: {
            ...((appTranslations as any).errors || {}),
            ...((sharedErrorTranslations as any).errors || {})
          },
          // Add job categories and positions translations at root level
          jobCategories: jobPositionsTranslations.jobCategories,
          jobPositions: jobPositionsTranslations.jobPositions,
          jobEducationLevels: educationLevelsTranslations.jobEducationLevels,
          jobEmploymentTypes: employmentTypesTranslations.jobEmploymentTypes,
          jobWorkplaceTypes: workplaceTypesTranslations.jobWorkplaceTypes,
          jobSalaryPeriods: salaryPeriodsTranslations.jobSalaryPeriods,
          locations: locationsTranslations.locations,
          skillCategories: skillsTranslations.skillCategories,
          skills: skillsTranslations.skills,
          languages: languagesTranslations.languages,
          // Add consent type translations at root level for direct transloco pipe usage
          ...consentTranslations,
          // Add deletion request status/type translations at root level
          ...{
            USER_ACCOUNT: deletionRequestTranslations.USER_ACCOUNT,
            TENANT_COMPANY: deletionRequestTranslations.TENANT_COMPANY,
            PENDING: deletionRequestTranslations.PENDING,
            PROCESSING: deletionRequestTranslations.PROCESSING,
            COMPLETED: deletionRequestTranslations.COMPLETED,
            FAILED: deletionRequestTranslations.FAILED
          },
          descriptions: {
            ...((appTranslations as any).descriptions || {}),
            ...consentTranslations.descriptions
          },
          // Merge common translations
          common: {
            ...((appTranslations as any).common || {}),
            ...deletionRequestTranslations.common
          },
          // Merge pages.settings from shared translations
          pages: {
            ...((appTranslations as any).pages || {}),
            settings: {
              ...((appTranslations as any).pages?.settings || {}),
              consents: {
                ...((appTranslations as any).pages?.settings?.consents || {}),
                ...consentTranslations.pages?.settings?.consents
              },
              deletionRequests: {
                ...((appTranslations as any).pages?.settings?.deletionRequests || {}),
                ...deletionRequestTranslations.pages?.settings?.deletionRequests
              }
            }
          },
          // Merge modals from shared translations
          modals: {
            ...((appTranslations as any).modals || {}),
            ...deletionRequestTranslations.modals
          }
        } as Translation;
      })
    );
  }
}
